import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Wand2, Loader2, Save, Download, Share2, RefreshCw, ChevronRight, 
  FileText, Clock, Building2, Calendar, AlertCircle, CheckCircle2,
  ArrowLeft, Plus, Trash2, Eye, Edit3, X, Search, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';

const QUICK_COMMANDS = {
  procurement: [
    { label: 'Full Procurement Schedule', template: 'Generate complete procurement schedule for all trades' },
    { label: 'For Specific Trade', template: 'Generate procurement schedule for {trade}' },
    { label: 'Critical Path Only', template: 'Generate procurement schedule for critical path activities only' },
  ],
  irs: [
    { label: 'Full IRS', template: 'Generate complete IRS for all trades' },
    { label: 'For Specific Trade', template: 'Generate IRS for {trade}' },
    { label: 'Critical Path Only', template: 'Generate IRS for critical path activities only' },
  ],
};

export function AICommandModal({ open, onClose, projectId, programmes, defaultType, onScheduleSaved, backgroundJobResult }) {
  const [step, setStep] = useState('command');
  const [scheduleType, setScheduleType] = useState(defaultType || 'procurement');
  const [selectedProgramme, setSelectedProgramme] = useState('');
  const [command, setCommand] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processingSeconds, setProcessingSeconds] = useState(0);
  const [activeJobId, setActiveJobId] = useState(null);
  const [draftResult, setDraftResult] = useState(null);
  const [draftItems, setDraftItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);

  const [saveName, setSaveName] = useState('');
  const [saveType, setSaveType] = useState('');
  const [saveTrade, setSaveTrade] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Dynamic trades from the selected programme
  const [programmeTrades, setProgrammeTrades] = useState([]);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [masterTrades, setMasterTrades] = useState([]);

  useEffect(() => {
    // Fetch master trade packages once
    api.get('/trade-packages')
      .then(res => setMasterTrades(res.data?.packages || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (open) {
      if (backgroundJobResult) {
        // Load the background job result directly into review step
        setDraftResult(backgroundJobResult);
        setDraftItems(backgroundJobResult.items || []);
        setRemovedItems([]);
        setSaveName(backgroundJobResult.trade || '');
        setSaveType(backgroundJobResult.schedule_type || defaultType || 'procurement');
        setSaveTrade(backgroundJobResult.trade || '');
        setScheduleType(backgroundJobResult.schedule_type || defaultType || 'procurement');
        setActiveJobId(backgroundJobResult._bgJobId || null);
        setStep('review');
        toast.success(`Background generation complete: ${(backgroundJobResult.items || []).length} items ready for review`);
      } else {
        setStep('command');
        setScheduleType(defaultType || 'procurement');
        setDraftResult(null);
        setDraftItems([]);
        setCommand('');
        setProcessing(false);
        setActiveJobId(null);
        setProgrammeTrades([]);
        if (programmes?.length === 1) {
          setSelectedProgramme(programmes[0].id);
        }
      }
    }
  }, [open, defaultType, programmes, backgroundJobResult]);

  // Fetch trades when programme is selected
  useEffect(() => {
    if (selectedProgramme && projectId) {
      setLoadingTrades(true);
      api.get(`/projects/${projectId}/programmes/${selectedProgramme}/trades`)
        .then(res => {
          setProgrammeTrades(res.data?.trades || []);
        })
        .catch(() => setProgrammeTrades([]))
        .finally(() => setLoadingTrades(false));
    }
  }, [selectedProgramme, projectId]);

  // Processing timer
  useEffect(() => {
    let timer;
    if (processing) {
      setProcessingSeconds(0);
      timer = setInterval(() => setProcessingSeconds(s => s + 1), 1000);
    } else {
      setProcessingSeconds(0);
    }
    return () => clearInterval(timer);
  }, [processing]);

  // Poll for background job completion
  useEffect(() => {
    if (!activeJobId || !processing) return;
    let cancelled = false;
    const poll = async () => {
      while (!cancelled) {
        await new Promise(r => setTimeout(r, 4000));
        if (cancelled) break;
        try {
          const res = await api.get(`/background-jobs/${activeJobId}`);
          const job = res.data;
          if (job.status === 'completed' && job.result) {
            if (!cancelled) {
              setDraftResult(job.result);
              setDraftItems(job.result.items || []);
              setRemovedItems([]);
              setSaveName(job.result.trade || '');
              setSaveType(job.result.schedule_type || scheduleType);
              setSaveTrade(job.result.trade || '');
              setStep('review');
              setProcessing(false);
              setActiveJobId(null);
              const stats = job.result.stats;
              const statsMsg = stats ? ` (${stats.total_in_programme} activities → ${stats.after_prefilter} filtered → ${stats.chunks_processed} batch${stats.chunks_processed > 1 ? 'es' : ''})` : '';
              toast.success(`Found ${(job.result.items || []).length} items${statsMsg}`);
            }
            return;
          } else if (job.status === 'failed') {
            if (!cancelled) {
              setProcessing(false);
              setActiveJobId(null);
              toast.error(job.error || 'AI generation failed. Please try again.');
            }
            return;
          }
        } catch {
          // Network error during poll - just retry
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [activeJobId, processing, scheduleType]);

  const handleSendToBackground = () => {
    // Job is already running in the background - just close the modal
    toast.success("AI generation continues in the background. You'll be notified when it's ready.", { duration: 6000 });
    setProcessing(false);
    // Keep activeJobId so backend keeps running, but stop polling
    setActiveJobId(null);
    onClose();
  };

  const handleGenerate = async () => {
    if (!command.trim()) { toast.error('Enter a command'); return; }
    if (!selectedProgramme) { toast.error('Select a programme'); return; }

    setProcessing(true);
    try {
      // Always use background job - it won't block the server
      const res = await api.post(`/projects/${projectId}/schedule/generate-background`, {
        command: command.trim(),
        programme_upload_id: selectedProgramme,
        schedule_type: scheduleType,
      });
      setActiveJobId(res.data.job_id);
      // Polling useEffect will handle the rest
    } catch (err) {
      setProcessing(false);
      const data = err.response?.data;
      const msg = data?.detail || data?.error || 'Failed to start generation.';
      toast.error(msg);
    }
  };

  const handleQuickCommand = (template) => {
    setCommand(template.replace('{trade}', '').replace('{area}', '').trim());
  };

  const handleSave = async () => {
    if (!saveName.trim()) { toast.error('Enter a schedule name'); return; }
    setSaving(true);
    try {
      const prog = programmes.find(p => p.id === selectedProgramme);
      const res = await api.post(`/projects/${projectId}/schedule/save`, {
        schedule_name: saveName.trim(),
        schedule_type: saveType || scheduleType,
        trade_package: saveTrade,
        description: saveDescription,
        items: draftItems,
        programme_id: selectedProgramme,
        programme_name: prog?.title || prog?.program_name || '',
        programme_revision: prog?.version || '',
        ai_command_used: command,
      });

      // Submit feedback for learning loop
      try {
        const feedbackItems = [
          ...draftItems.map(item => ({
            action: 'accepted',
            trade: item.trade || saveTrade,
            description: item.description || item.information_required || '',
            linked_activity: item.linked_activity || '',
            activity_id: item.activity_id || '',
            procurement_class: item.procurement_class || null,
            confidence: item.confidence || null,
            information_required: item.information_required || null,
            consultant_responsible: item.consultant_responsible || null,
            information_type: item.information_type || null,
          })),
          ...removedItems.map(item => ({
            action: 'rejected',
            trade: item.trade || saveTrade,
            description: item.description || item.information_required || '',
            linked_activity: item.linked_activity || '',
            activity_id: item.activity_id || '',
            procurement_class: item.procurement_class || null,
            confidence: item.confidence || null,
            reason: 'Removed during review',
            information_required: item.information_required || null,
            consultant_responsible: item.consultant_responsible || null,
            information_type: item.information_type || null,
          })),
        ];

        if (feedbackItems.length > 0) {
          await api.post('/ai-feedback/submit', {
            project_id: projectId,
            schedule_type: saveType || scheduleType,
            trade_package: saveTrade,
            programme_id: selectedProgramme,
            ai_command: command,
            items: feedbackItems,
          });
        }
      } catch (feedbackErr) {
        // Don't block save if feedback fails
        console.warn('Feedback submission failed:', feedbackErr);
      }

      toast.success('Schedule saved to register');
      onScheduleSaved?.(res.data.schedule, activeJobId);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const removeItem = (idx) => {
    const removed = draftItems[idx];
    if (removed) {
      setRemovedItems(prev => [...prev, removed]);
    }
    setDraftItems(prev => prev.filter((_, i) => i !== idx));
  };

  const isProcurement = scheduleType === 'procurement';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col" data-testid="ai-command-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-indigo-600" />
            {step === 'command' && (scheduleType === 'irs' ? 'AI IRS Generator' : 'AI Procurement Schedule Generator')}
            {step === 'review' && 'Review Generated Draft'}
            {step === 'save' && 'Save Schedule'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {step === 'command' && (
            <div className="space-y-4 p-1">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Programme</label>
                <Select value={selectedProgramme} onValueChange={setSelectedProgramme}>
                  <SelectTrigger data-testid="programme-select-modal"><SelectValue placeholder="Select programme..." /></SelectTrigger>
                  <SelectContent>
                    {(programmes || []).map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.title || p.program_name} {p.version ? `(${p.version})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Quick Commands</label>
                <div className="flex flex-wrap gap-2">
                  {QUICK_COMMANDS[scheduleType]?.map((cmd, i) => (
                    <Button key={i} variant="outline" size="sm" className="text-xs" onClick={() => handleQuickCommand(cmd.template)}>
                      {cmd.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Command</label>
                <Textarea
                  value={command}
                  onChange={(e) => setCommand(e.target.value)}
                  placeholder={isProcurement 
                    ? 'e.g. "Generate lead times for HVAC based on the programme"'
                    : 'e.g. "Generate IRS for electrical works"'
                  }
                  rows={3}
                  data-testid="ai-command-textarea"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">
                  {loadingTrades ? 'Loading trades from programme...' : programmeTrades.length > 0 ? 'Trades found in programme (click to generate)' : selectedProgramme ? 'No specific trades detected — select a programme or type a command' : 'Select a programme to see available trades'}
                </label>
                <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
                  {loadingTrades ? (
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <Loader2 className="h-4 w-4 animate-spin" /> Analyzing programme...
                    </div>
                  ) : programmeTrades.length > 0 ? (
                    <>
                      <Badge
                        variant="outline"
                        className="cursor-pointer hover:bg-indigo-100 hover:border-indigo-400 bg-indigo-50 border-indigo-300 text-indigo-700 text-xs font-semibold"
                        onClick={() => setCommand(`Generate complete ${isProcurement ? 'procurement schedule' : 'IRS'} for ALL trades`)}
                      >
                        All Trades
                      </Badge>
                      {programmeTrades.map(t => (
                        <Badge
                          key={t}
                          variant="outline"
                          className="cursor-pointer hover:bg-indigo-50 hover:border-indigo-300 text-xs"
                          onClick={() => setCommand(`Generate ${isProcurement ? 'procurement schedule' : 'IRS'} for ${t}`)}
                        >
                          {t}
                        </Badge>
                      ))}
                    </>
                  ) : !selectedProgramme && masterTrades.length > 0 ? (
                    <>
                      <p className="text-xs text-slate-400 w-full mb-1">All available trade packages:</p>
                      {masterTrades.map(t => (
                        <Badge
                          key={t}
                          variant="outline"
                          className="cursor-pointer hover:bg-slate-50 hover:border-slate-300 text-xs text-slate-500"
                          onClick={() => setCommand(`Generate ${isProcurement ? 'procurement schedule' : 'IRS'} for ${t}`)}
                        >
                          {t}
                        </Badge>
                      ))}
                    </>
                  ) : (
                    <p className="text-xs text-slate-400">
                      {selectedProgramme ? 'Type a trade name in the command box above, e.g. "Generate IRS for Brickwork"' : 'Select a programme first'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 'review' && draftResult && (
            <div className="space-y-3 p-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <Button variant="ghost" size="sm" onClick={() => setStep('command')} className="self-start">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                <div className="flex items-center gap-1.5 flex-wrap text-sm text-slate-500">
                  <span className="text-xs">{draftItems.length} items</span>
                  <Badge variant="secondary" className="text-[10px]">{draftResult.trade}</Badge>
                  {draftResult.extraction_mode && (
                    <Badge variant="outline" className="text-[10px] text-green-700 border-green-300 bg-green-50">{draftResult.extraction_mode}</Badge>
                  )}
                  <Badge variant="outline" className="text-[10px] truncate max-w-[120px] sm:max-w-none">{draftResult.programme_name}</Badge>
                </div>
              </div>

              {/* Processing Stats */}
              {draftResult.stats && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-400 px-1">
                  <span>Programme: {draftResult.stats.total_in_programme} activities</span>
                  <span>Pre-filtered: {draftResult.stats.after_prefilter}</span>
                  <span>Batches: {draftResult.stats.chunks_processed}</span>
                </div>
              )}

              {/* Review Required Items */}
              {draftResult.review_required?.length > 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-amber-800 mb-1.5 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> Review Required ({draftResult.review_required.length})</p>
                  <div className="space-y-1">
                    {draftResult.review_required.map((r, i) => (
                      <div key={i} className="text-xs text-amber-700">
                        <span className="font-mono">{r.activity_id || '-'}</span> — {r.activity_name || r.reason_ambiguous}
                        {r.what_needs_confirmation && <span className="text-amber-500 ml-1">({r.what_needs_confirmation})</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Excluded Summary */}
              {draftResult.excluded_summary && (
                <p className="text-xs text-slate-400 px-1">{draftResult.excluded_summary}</p>
              )}

              {/* Ordinary Supply Excluded (Procurement only) */}
              {draftResult.ordinary_supply_excluded?.length > 0 && (
                <div className="border border-slate-200 bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-slate-600 mb-1.5">Ordinary Supply Items Excluded ({draftResult.ordinary_supply_excluded.length})</p>
                  <div className="flex flex-wrap gap-1.5">
                    {draftResult.ordinary_supply_excluded.map((item, i) => (
                      <span key={i} className="text-[10px] bg-white border border-slate-200 text-slate-500 px-2 py-0.5 rounded" title={item.reason}>
                        {item.item}
                      </span>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1.5">These are standard commodities excluded from managed procurement tracking.</p>
                </div>
              )}

              <div className="border rounded-lg overflow-x-auto max-h-[50vh]">
                <table className="w-full text-xs min-w-[900px]">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-medium text-slate-600">#</th>
                      {isProcurement ? (
                        <>
                          <th className="p-2 text-left font-medium text-slate-600">Description</th>
                          <th className="p-2 text-left font-medium text-slate-600">Class</th>
                          <th className="p-2 text-left font-medium text-slate-600">Area</th>
                          <th className="p-2 text-left font-medium text-slate-600">Activity ID</th>
                          <th className="p-2 text-left font-medium text-slate-600">Site Required</th>
                          <th className="p-2 text-left font-medium text-slate-600">Order By</th>
                          <th className="p-2 text-left font-medium text-slate-600">Lead (wks)</th>
                          <th className="p-2 text-left font-medium text-slate-600">Conf.</th>
                        </>
                      ) : (
                        <>
                          <th className="p-2 text-left font-medium text-slate-600">Information Required</th>
                          <th className="p-2 text-left font-medium text-slate-600">Description</th>
                          <th className="p-2 text-left font-medium text-slate-600">Area</th>
                          <th className="p-2 text-left font-medium text-slate-600">Activity ID</th>
                          <th className="p-2 text-left font-medium text-slate-600">Required By</th>
                          <th className="p-2 text-left font-medium text-slate-600">Priority</th>
                          <th className="p-2 text-left font-medium text-slate-600">Consultant</th>
                          <th className="p-2 text-left font-medium text-slate-600">Conf.</th>
                        </>
                      )}
                      <th className="p-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {draftItems.map((item, idx) => (
                      <tr key={idx} className="border-t hover:bg-slate-50">
                        <td className="p-2 text-slate-400">{item.item_no || item.irs_no || idx+1}</td>
                        {isProcurement ? (
                          <>
                            <td className="p-2 max-w-[200px] truncate" title={item.description}>
                              {item.description}
                              {item.shop_drawing_required && <span className="ml-1 text-[9px] text-indigo-500" title="Shop drawing required">SD</span>}
                            </td>
                            <td className="p-2">
                              {item.procurement_class && (
                                <Badge variant="outline" className={`text-[9px] whitespace-nowrap ${
                                  item.procurement_class === 'LONG_LEAD_PROCUREMENT' ? 'text-red-700 border-red-300 bg-red-50' :
                                  item.procurement_class === 'SPECIALIST_APPOINTMENT' ? 'text-purple-700 border-purple-300 bg-purple-50' :
                                  item.procurement_class === 'MANAGED_PROCUREMENT' ? 'text-blue-700 border-blue-300 bg-blue-50' :
                                  'text-slate-500 border-slate-300'
                                }`}>
                                  {item.procurement_class.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                                </Badge>
                              )}
                            </td>
                            <td className="p-2 text-slate-500">{item.area_zone_level || '-'}</td>
                            <td className="p-2 font-mono text-slate-500">{item.activity_id}</td>
                            <td className="p-2">{item.required_on_site_date || item.site_required_date || '-'}</td>
                            <td className="p-2">{item.order_by_date || item.required_order_date || '-'}</td>
                            <td className="p-2 text-slate-500">{item.total_lead_time_weeks || '-'}</td>
                            <td className="p-2">
                              {item.confidence && (
                                <Badge variant="outline" className={`text-[9px] ${item.confidence === 'HIGH' ? 'text-green-700 border-green-300' : item.confidence === 'MEDIUM' ? 'text-amber-700 border-amber-300' : 'text-red-700 border-red-300'}`}>
                                  {item.confidence}
                                </Badge>
                              )}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="p-2 max-w-[180px] truncate">{item.information_required}</td>
                            <td className="p-2 max-w-[180px] truncate" title={item.description_reason}>{item.description_reason}</td>
                            <td className="p-2 text-slate-500">{item.area_zone_level || '-'}</td>
                            <td className="p-2 font-mono text-slate-500">{item.activity_id}</td>
                            <td className="p-2">{item.information_required_by || '-'}</td>
                            <td className="p-2">
                              <Badge variant={item.priority === 'high' ? 'destructive' : 'secondary'} className="text-[10px]">
                                {item.priority}
                              </Badge>
                            </td>
                            <td className="p-2 text-slate-500">{item.consultant_responsible || '-'}</td>
                            <td className="p-2">
                              {item.confidence && (
                                <Badge variant="outline" className={`text-[9px] ${item.confidence === 'HIGH' ? 'text-green-700 border-green-300' : item.confidence === 'MEDIUM' ? 'text-amber-700 border-amber-300' : 'text-red-700 border-red-300'}`}>
                                  {item.confidence}
                                </Badge>
                              )}
                            </td>
                          </>
                        )}
                        <td className="p-2">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeItem(idx)}>
                            <X className="h-3 w-3 text-red-400" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === 'save' && (
            <div className="space-y-4 p-1">
              <Button variant="ghost" size="sm" onClick={() => setStep('review')}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to Draft
              </Button>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Schedule Name *</label>
                  <Input
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder="e.g. HVAC"
                    data-testid="save-schedule-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Schedule Type</label>
                  <Select value={saveType || scheduleType} onValueChange={setSaveType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="procurement">Procurement Schedule</SelectItem>
                      <SelectItem value="irs">IRS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Trade / Package</label>
                  <Input value={saveTrade} onChange={(e) => setSaveTrade(e.target.value)} placeholder="e.g. HVAC" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Programme</label>
                  <Input value={draftResult?.programme_name || ''} disabled className="bg-slate-50" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Description / Notes</label>
                <Textarea value={saveDescription} onChange={(e) => setSaveDescription(e.target.value)} rows={2} placeholder="Optional notes..." />
              </div>
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-3 text-xs text-slate-600">
                  <p className="font-medium mb-1">Auto-generated name:</p>
                  <p className="text-indigo-700 font-mono">
                    {saveName || '[Name]'} - {saveType === 'irs' ? 'IRS' : 'Procurement Schedule'} - {draftResult?.programme_name || '[Programme]'} - Rev {draftResult?.programme_revision || '0'}
                  </p>
                  <p className="mt-2">{draftItems.length} items will be saved</p>
                  {removedItems.length > 0 && (
                    <p className="mt-1 text-indigo-600">
                      AI will learn from your {removedItems.length} correction{removedItems.length > 1 ? 's' : ''} to improve future generations.
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          {step === 'command' && (
            <div className="flex flex-col gap-2 w-full">
              {processing && processingSeconds >= 5 && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 sm:px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                    <span className="text-xs sm:text-sm text-amber-800">
                      Processing... {Math.floor(processingSeconds / 60)}:{String(processingSeconds % 60).padStart(2, '0')}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSendToBackground}
                    className="border-amber-300 text-amber-800 hover:bg-amber-100 text-xs"
                    data-testid="process-background-btn"
                  >
                    <Clock className="h-3.5 w-3.5 mr-1.5" />
                    Check Back Later
                  </Button>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { if (processing) handleSendToBackground(); else onClose(); }}>
                  {processing ? 'Check Back Later' : 'Cancel'}
                </Button>
                <Button onClick={handleGenerate} disabled={processing || !command.trim() || !selectedProgramme} data-testid="generate-draft-btn">
                  {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                  Generate Draft
                </Button>
              </div>
            </div>
          )}
          {step === 'review' && (
            <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:justify-between">
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span>{draftItems.length} items</span>
                {removedItems.length > 0 && (
                  <span className="text-amber-500">{removedItems.length} removed</span>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
                <Button size="sm" onClick={() => setStep('save')} disabled={draftItems.length === 0} data-testid="proceed-to-save-btn">
                  <Save className="h-4 w-4 mr-1" /> Save Schedule
                </Button>
              </div>
            </div>
          )}
          {step === 'save' && (
            <div className="flex gap-2 w-full justify-end">
              <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving || !saveName.trim()} data-testid="confirm-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save to Register
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ScheduleRegister({ projectId, scheduleType, programmes, onOpenSchedule, onRefresh }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(scheduleType || '');
  const [deletingId, setDeletingId] = useState(null);

  const loadSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (typeFilter) params.append('schedule_type', typeFilter);
      if (search) params.append('search', search);
      const res = await api.get(`/projects/${projectId}/schedule/register?${params}`);
      setSchedules(res.data);
    } catch (err) {
      console.error('Failed to load schedules:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, typeFilter, search]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const handleDelete = async (e, scheduleId, scheduleName) => {
    e.stopPropagation();
    if (!window.confirm(`Delete schedule "${scheduleName}"? This will also remove any promoted procurement items from this schedule.`)) return;
    setDeletingId(scheduleId);
    try {
      await api.delete(`/projects/${projectId}/schedule/${scheduleId}`);
      toast.success(`Schedule "${scheduleName}" deleted`);
      loadSchedules();
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to delete schedule');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="schedule-register">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Saved Schedules</h3>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-8 h-8 w-full sm:w-48 text-xs"
            />
          </div>
          {!scheduleType && (
            <Select value={typeFilter || 'all'} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 w-32 text-xs"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="procurement">Procurement</SelectItem>
                <SelectItem value="irs">IRS</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-400"><Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" /><p className="text-sm">Loading schedules...</p></div>
      ) : schedules.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <FileText className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No saved schedules yet</p>
          <p className="text-xs mt-1">Use AI Command to generate your first schedule</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 text-xs font-medium text-slate-600">Schedule Name</th>
                <th className="text-left p-3 text-xs font-medium text-slate-600">Type</th>
                <th className="text-left p-3 text-xs font-medium text-slate-600">Trade</th>
                <th className="text-left p-3 text-xs font-medium text-slate-600 hidden lg:table-cell">Subcontractor</th>
                <th className="text-left p-3 text-xs font-medium text-slate-600">Programme</th>
                <th className="text-left p-3 text-xs font-medium text-slate-600">Rev</th>
                <th className="text-left p-3 text-xs font-medium text-slate-600">Items</th>
                <th className="text-left p-3 text-xs font-medium text-slate-600">Status</th>
                <th className="p-3 w-16 text-xs font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((sched) => (
                <tr
                  key={sched.id}
                  className="border-t hover:bg-slate-50 cursor-pointer"
                  onClick={() => onOpenSchedule?.(sched)}
                  data-testid={`schedule-row-${sched.id}`}
                >
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{sched.schedule_name}</span>
                      {sched.newer_programme_available && (
                        <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">
                          Update Available
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge variant="secondary" className={sched.schedule_type === 'procurement' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                      {sched.schedule_type === 'procurement' ? 'Procurement' : 'IRS'}
                    </Badge>
                  </td>
                  <td className="p-3 text-slate-600">{sched.trade_package || '-'}</td>
                  <td className="p-3 text-slate-500 text-xs hidden lg:table-cell">
                    {sched.linked_subcontractor?.company_name || <span className="text-slate-300 italic">Not linked</span>}
                  </td>
                  <td className="p-3 text-slate-500 text-xs max-w-[150px] truncate">{sched.programme_name || '-'}</td>
                  <td className="p-3 text-slate-500">Rev {sched.revision}</td>
                  <td className="p-3 text-slate-600">{sched.item_count}</td>
                  <td className="p-3">
                    <Badge variant={sched.status === 'published' ? 'default' : 'secondary'} className="text-[10px]">
                      {sched.status}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button
                        className="p-1 hover:bg-red-50 rounded text-slate-400 hover:text-red-600 transition-colors"
                        onClick={(e) => handleDelete(e, sched.id, sched.schedule_name)}
                        disabled={deletingId === sched.id}
                        data-testid={`delete-schedule-${sched.id}`}
                        title="Delete schedule"
                      >
                        {deletingId === sched.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                      <ChevronRight className="h-4 w-4 text-slate-300" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ScheduleDetailView({ projectId, schedule, programmes, onBack, onRefresh }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareName, setShareName] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    loadDetail();
  }, [schedule?.id]);

  const loadDetail = async () => {
    if (!schedule?.id) return;
    try {
      setLoading(true);
      const res = await api.get(`/projects/${projectId}/schedule/${schedule.id}`);
      setItems(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!shareEmail.trim()) { toast.error('Enter an email'); return; }
    setSharing(true);
    try {
      await api.post(`/projects/${projectId}/schedule/${schedule.id}/share`, {
        recipients: [{ email: shareEmail, name: shareName, permission: sharePermission }],
        notify: true,
      });
      toast.success(`Shared with ${shareEmail}`);
      setShareOpen(false);
      setShareEmail('');
      setShareName('');
    } catch (err) {
      toast.error('Failed to share');
    } finally {
      setSharing(false);
    }
  };

  const handleCompare = async () => {
    const latest = programmes?.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
    if (!latest || latest.id === schedule.programme_id) {
      toast.info('Already using the latest programme');
      return;
    }
    try {
      const res = await api.post(`/projects/${projectId}/schedule/${schedule.id}/compare`, {
        programme_id: latest.id,
      });
      const d = res.data;
      toast.info(`Comparison: ${d.activities_added} added, ${d.activities_removed} removed, ${d.date_changes} date changes`);
    } catch (err) {
      toast.error('Comparison failed');
    }
  };

  const isProcurement = schedule?.schedule_type === 'procurement';

  return (
    <div className="space-y-4" data-testid="schedule-detail-view">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={onBack} className="flex-shrink-0"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900 text-sm sm:text-base truncate">{schedule?.schedule_name}</h3>
            <p className="text-xs text-slate-500 truncate">
              {schedule?.auto_name || `${schedule?.trade_package} - ${isProcurement ? 'Procurement' : 'IRS'} - Rev ${schedule?.revision}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={handleCompare}>
            <RefreshCw className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Compare</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShareOpen(true)}>
            <Share2 className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Share</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <Card><CardContent className="p-2 sm:p-3">
          <p className="text-[10px] text-slate-500 uppercase">Type</p>
          <p className="text-sm font-medium">{isProcurement ? 'Procurement' : 'IRS'}</p>
        </CardContent></Card>
        <Card><CardContent className="p-2 sm:p-3">
          <p className="text-[10px] text-slate-500 uppercase">Items</p>
          <p className="text-sm font-medium">{items.length}</p>
        </CardContent></Card>
        <Card><CardContent className="p-2 sm:p-3">
          <p className="text-[10px] text-slate-500 uppercase">Programme</p>
          <p className="text-sm font-medium truncate">{schedule?.programme_name || '-'}</p>
        </CardContent></Card>
        <Card><CardContent className="p-2 sm:p-3">
          <p className="text-[10px] text-slate-500 uppercase">Generated</p>
          <p className="text-sm font-medium">{schedule?.generated_at ? new Date(schedule.generated_at).toLocaleDateString('en-GB') : '-'}</p>
        </CardContent></Card>
      </div>

      {schedule?.shared_with?.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Share2 className="h-3.5 w-3.5" />
          Shared with: {schedule.shared_with.map(s => s.name || s.email).join(', ')}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-x-auto max-h-[55vh]">
          <table className="w-full text-xs min-w-[800px]">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="p-2 text-left font-medium text-slate-600">#</th>
                {isProcurement ? (
                  <>
                    <th className="p-2 text-left font-medium text-slate-600">Description</th>
                    <th className="p-2 text-left font-medium text-slate-600">Area</th>
                    <th className="p-2 text-left font-medium text-slate-600">Activity</th>
                    <th className="p-2 text-left font-medium text-slate-600">Site Required</th>
                    <th className="p-2 text-left font-medium text-slate-600">Approval By</th>
                    <th className="p-2 text-left font-medium text-slate-600">Order By</th>
                    <th className="p-2 text-left font-medium text-slate-600">Mfg Lead</th>
                    <th className="p-2 text-left font-medium text-slate-600">Lead Time</th>
                    <th className="p-2 text-left font-medium text-slate-600">Delivery</th>
                    <th className="p-2 text-left font-medium text-slate-600">Status</th>
                  </>
                ) : (
                  <>
                    <th className="p-2 text-left font-medium text-slate-600">Information Required</th>
                    <th className="p-2 text-left font-medium text-slate-600">Description</th>
                    <th className="p-2 text-left font-medium text-slate-600">Area</th>
                    <th className="p-2 text-left font-medium text-slate-600">Activity</th>
                    <th className="p-2 text-left font-medium text-slate-600">Required By</th>
                    <th className="p-2 text-left font-medium text-slate-600">Priority</th>
                    <th className="p-2 text-left font-medium text-slate-600">Consultant</th>
                    <th className="p-2 text-left font-medium text-slate-600">Status</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className="border-t hover:bg-slate-50">
                  <td className="p-2 text-slate-400">{item.item_no || item.irs_no || idx+1}</td>
                  {isProcurement ? (
                    <>
                      <td className="p-2 max-w-[200px] truncate">{item.description}</td>
                      <td className="p-2 text-slate-500">{item.area_zone_level || '-'}</td>
                      <td className="p-2 font-mono text-slate-500">{item.activity_id}</td>
                      <td className="p-2">{item.site_required_date || '-'}</td>
                      <td className="p-2">{item.required_approval_date || '-'}</td>
                      <td className="p-2">{item.required_order_date || '-'}</td>
                      <td className="p-2 text-slate-500">{item.manufacturing_lead_time || '-'}</td>
                      <td className="p-2 text-slate-500">{item.total_lead_time_weeks ? `${item.total_lead_time_weeks} wks` : item.lead_time_value ? `${item.lead_time_value} ${item.lead_time_unit === 'days' ? 'days' : 'wks'}` : '-'}</td>
                      <td className="p-2 text-slate-500">{item.delivery_period || '-'}</td>
                      <td className="p-2"><Badge variant="secondary" className="text-[10px]">{item.status}</Badge></td>
                    </>
                  ) : (
                    <>
                      <td className="p-2 max-w-[180px] truncate">{item.information_required}</td>
                      <td className="p-2 max-w-[180px] truncate">{item.description_reason}</td>
                      <td className="p-2 text-slate-500">{item.area_zone_level || '-'}</td>
                      <td className="p-2 font-mono text-slate-500">{item.activity_id}</td>
                      <td className="p-2">{item.information_required_by || '-'}</td>
                      <td className="p-2"><Badge variant={item.priority === 'high' ? 'destructive' : 'secondary'} className="text-[10px]">{item.priority}</Badge></td>
                      <td className="p-2 text-slate-500">{item.consultant_responsible || '-'}</td>
                      <td className="p-2"><Badge variant="secondary" className="text-[10px]">{item.status}</Badge></td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Share Schedule</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Recipient Name</label>
              <Input value={shareName} onChange={(e) => setShareName(e.target.value)} placeholder="Name" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Email</label>
              <Input value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} placeholder="email@example.com" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Permission</label>
              <Select value={sharePermission} onValueChange={setSharePermission}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">View only</SelectItem>
                  <SelectItem value="comment">Comment</SelectItem>
                  <SelectItem value="edit">Edit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareOpen(false)}>Cancel</Button>
            <Button onClick={handleShare} disabled={sharing}>
              {sharing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Share2 className="h-4 w-4 mr-2" />}
              Share
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AICommandModal;
